// src/components/common/LightboxGallery.js
import React, { useEffect, useState, useRef } from 'react';
import './LightboxGallery.css'; // Important d'importer le CSS

function LightboxGallery({ images, currentIndex, onClose, onNext, onPrev }) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    // S'assure que les props sont valides avant d'ajouter l'écouteur
    if (!images || images.length === 0 || currentIndex === null || currentIndex < 0) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      } else if (event.key === 'ArrowRight' && onNext && currentIndex < images.length - 1) {
        onNext();
      } else if (event.key === 'ArrowLeft' && onPrev && currentIndex > 0) {
        onPrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [images, currentIndex, onClose, onNext, onPrev]); // Dépendances

  // Effect for image loading and initial lightbox focus
  useEffect(() => {
    if (currentIndex !== null && images && images.length > 0 && currentIndex < images.length) {
      setIsImageLoaded(false); // Reset for fade-in effect on new image
      // Focus the lightbox for accessibility (e.g., keyboard nav after opening)
      if (overlayRef.current) {
        overlayRef.current.focus();
      }
    }
  }, [currentIndex, images]);

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to finish before calling onClose
    setTimeout(() => {
      onClose();
      setIsClosing(false); // Reset for next time
    }, 300); // Match animation duration (0.3s)
  };

  const handlePrevClick = (e) => {
    e.stopPropagation(); // Empêche la fermeture de la lightbox si on clique sur le bouton
    onPrev && onPrev();
  };

  const handleNextClick = (e) => {
    e.stopPropagation();
    onNext && onNext();
  };

  // Retour anticipé si les props ne sont pas valides pour l'affichage
  if (!images || images.length === 0 || currentIndex === null || currentIndex < 0 || currentIndex >= images.length) {
    return null;
  }

  const currentImage = images[currentIndex];
  // Supporte les images sous forme de chaînes (URL) ou d'objets { src, alt, caption }
  const currentImageSrc = typeof currentImage === 'string' ? currentImage : currentImage.src;
  const currentImageAlt = typeof currentImage === 'string' ? `Galerie image ${currentIndex + 1}` : currentImage.alt || `Galerie image ${currentIndex + 1}`;
  const currentImageCaption = typeof currentImage === 'object' ? currentImage.caption : null;


  return (
    <div
      ref={overlayRef}
      className={`lightbox-overlay ${isClosing ? 'lightbox-fade-out' : 'lightbox-fade-in'}`}
      onClick={handleClose} // Ferme la lightbox si on clique sur l'overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="lightbox-caption-text"
      tabIndex="-1" // Make it focusable
    >
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}> {/* Empêche la fermeture si on clique DANS le contenu */}
        <button
          className="lightbox-control lightbox-close"
          onClick={handleClose} // Le bouton close appelle directement handleClose
          aria-label="Fermer la visionneuse"
        >
          &times;
        </button>

        {/* Flèche Précédent */}
        {images.length > 1 && currentIndex > 0 && (
          <button
            className="lightbox-control lightbox-prev"
            onClick={handlePrevClick}
            aria-label="Image précédente"
          >
            &#10094;
          </button>
        )}
        
        <div className="lightbox-image-container">
          <img
            src={currentImageSrc}
            alt={currentImageAlt}
            className={`lightbox-image ${isImageLoaded ? 'loaded' : ''}`}
            onLoad={() => setIsImageLoaded(true)}
            onError={() => setIsImageLoaded(true)} // Marquer comme chargée même en cas d'erreur pour cacher le spinner
          />
          {!isImageLoaded && <div className="lightbox-spinner"></div>}
        </div>
        
        {/* Flèche Suivant */}
        {images.length > 1 && currentIndex < images.length - 1 && (
          <button
            className="lightbox-control lightbox-next"
            onClick={handleNextClick}
            aria-label="Image suivante"
          >
            &#10095;
          </button>
        )}

        <div className="lightbox-caption" id="lightbox-caption-text">
          {currentImageCaption && (
            <p className="lightbox-image-custom-caption">{currentImageCaption}</p>
          )}
          <p>
            Image {currentIndex + 1} sur {images.length}
          </p>
        </div>
      </div>
    </div>
  );
}

export default LightboxGallery;